/**
 * Notificação LOCAL de fim de sessão de Foco (BUG-204 / F5).
 *
 * Este módulo é o ÚNICO importador de `@capacitor/local-notifications` (guard
 * estrutural em `tests/unit/focusNotificationConfig.test.ts`) e o dono único do
 * agendamento.
 *
 * ── Por que LOCAL e não push do servidor ────────────────────────────────────
 *
 * A pergunta do fundador foi exatamente essa, e a resposta veio de medição em
 * produção (03/09): **24 das 154 contas têm token FCM — 15,6%.** Um aviso
 * server-side não alcançaria 84% das pessoas, e dependeria de uma cadeia que
 * este projeto já viu falhar em silêncio (o apagão de três dias por credencial
 * APNs faltando, BUG-169). A notificação local não precisa de token, de FCM nem
 * de rede: o sistema a guarda e dispara no horário exato **mesmo com o app
 * morto** — precisa só da permissão. E custa zero de infra: agendar push por
 * sessão exigiria Cloud Tasks ou um cron varrendo a base minuto a minuto.
 *
 * ── O que ela substitui, e por que a anterior era inalcançável ──────────────
 *
 * `notifyFocusSessionDone` (web) sai fora em três guardas, e as três falham no
 * WKWebView: `Notification` não existe, `Notification.permission` idem, e o
 * `scripts/mobile-postbuild.mjs` **remove o service worker do bundle nativo de
 * propósito**. Ou seja, no app das lojas aquele caminho é código morto — tem
 * teste, tem copy, e nunca emitiu uma notificação. Ele continua servindo a web,
 * onde funciona; este módulo é o par nativo.
 *
 * ── As regras que não podem cair ────────────────────────────────────────────
 *
 * 1. **Falha aqui NUNCA alcança a sessão.** Notificação é aviso; a sessão é
 *    economia (moedas, XP, liga). Toda chamada ao plugin é engolida com
 *    `console.warn` — o pior estado possível é "o aviso não chegou", jamais
 *    "a sessão quebrou". Mesma doutrina da Live Activity.
 *
 * 2. **O vencimento tem UM dono, e é `computeDueMs`.** É a mesma função que
 *    agenda a conclusão no client e que alimenta o `staleDate` da Ilha.
 *    Recalcular a data aqui criaria uma quarta derivação de `startedAt` — a
 *    doença que o `focusElapsed.ts` nasceu para curar. E ela devolve `null` com
 *    a sessão pausada ou sem alvo, o que dá de graça as duas regras que a
 *    notificação precisa: **sessão pausada não tem aviso agendado** (retomar
 *    reagenda com o vencimento empurrado) e **sessão livre nunca agenda**, já
 *    que não existe instante para avisar.
 *
 * 3. **Um id FIXO, não um por sessão.** O produto tem no máximo uma sessão de
 *    Foco por vez (o servidor recusa a segunda), então id por sessão só
 *    acrescentaria a chance de deixar um agendamento órfão de uma sessão que
 *    ninguém mais consulta. Com id fixo, agendar a próxima **substitui** a
 *    anterior por construção — é a mesma propriedade que a varredura de
 *    activities do BUG-163 teve de reconquistar com código.
 *
 * 4. **Uma convergência por vez.** O chamador dispara com `void` e o
 *    `useFocusTimer` grava a sessão duas vezes por transição (otimista +
 *    confirmação do servidor). Sem fila, duas convergências se cruzam entre o
 *    cancelamento e o agendamento — exatamente a janela que empilhou cinco
 *    Ilhas em 21/08.
 */

import { LocalNotifications } from "@capacitor/local-notifications";
import { isNativeApp } from "../../../lib/platform/native";
import { computeDueMs, type FocusPauseState } from "./focusElapsed";

/**
 * Interruptor de ROLLBACK — constante local, nunca config remota (a doutrina do
 * `pushPlatformGate`: config que chega vazia vira liberação acidental).
 * `false` = nenhuma chamada ao plugin, e o app volta ao comportamento de antes
 * desta fatia sem precisar de build de emergência.
 */
export const FOCUS_LOCAL_NOTIFICATION_READY = true;

/**
 * Id fixo do aviso de fim de sessão — ver a regra 3 do cabeçalho.
 *
 * O número não tem significado; o que importa é ser **estável entre execuções**
 * e não colidir com outro agendamento do app. Hoje este é o único agendamento
 * local que existe.
 */
export const FOCUS_DONE_NOTIFICATION_ID = 8801;

/** Grupo de ações da notificação (registrado uma vez por processo). */
export const FOCUS_DONE_ACTION_TYPE = "FOCO_CONCLUIDO";

/** A ação explícita "Marcar páginas" — o mesmo destino do toque no corpo. */
export const FOCUS_DONE_ACTION_PAGES = "marcar-paginas";

/** A foto que o chamador entrega; o módulo decide o que fazer com ela. */
export interface FocusNotificationSnapshot {
  sessionId: string;
  /** Título do livro, para o corpo dizer sobre o quê a sessão foi. */
  bookTitle: string | null;
  startedAtMs: number;
  pausedAtMs: number | null;
  pausedTotalSec: number;
  /** 0 = sessão livre: sem alvo, logo sem instante para avisar. */
  targetDurationSec: number;
}

/**
 * Copy do aviso. Pura e exportada — é o que permite travá-la sem plugin, e o
 * que impede que ela seja escrita duas vezes (a doença do BUG-81 em copy).
 *
 * ⚠️ Ela promete o que a tela ENTREGA: com a F1, abrir o app numa sessão
 * vencida cai no wrap-up expandido. Prometer "marque suas páginas" sem esse
 * caminho seria a classe BUG-58 — o aviso mandando a pessoa a uma tela onde a
 * ação não está.
 */
export function focusDoneNotificationCopy(bookTitle: string | null): {
  title: string;
  body: string;
} {
  const titulo = bookTitle?.trim();
  return {
    title: "Sessão de foco concluída",
    body: titulo
      ? `Quantas páginas de ${titulo} você leu? Toque para registrar.`
      : "Quantas páginas você leu? Toque para registrar.",
  };
}

/* ── Decisão pura: existe aviso a agendar, e para quando? ─────────────────── */

/**
 * `null` = nada a agendar (cancelar o que houver).
 *
 * Extraída porque dentro do módulo com plugin não haveria como exercitá-la, e
 * porque as três razões de não agendar são diferentes entre si e todas
 * legítimas: sessão ausente, sessão pausada (`computeDueMs` devolve `null`) e
 * vencimento no passado — este último é o caso de o app abrir depois do fim,
 * quando o aviso já não tem função e o wrap-up está na tela.
 */
export function resolveFocusNotificationAt(
  snapshot: FocusNotificationSnapshot | null,
  nowMs: number,
): number | null {
  if (!snapshot) return null;
  const pause: FocusPauseState = {
    startedAtMs: snapshot.startedAtMs,
    pausedAtMs: snapshot.pausedAtMs,
    pausedTotalSec: snapshot.pausedTotalSec,
  };
  const dueMs = computeDueMs(pause, snapshot.targetDurationSec);
  if (dueMs == null) return null;
  // Vencimento no passado: o app está aberto na hora do fim, e quem avisa é a
  // própria tela. Agendar para trás dispararia imediatamente, com a pessoa
  // olhando o wrap-up.
  if (dueMs <= nowMs) return null;
  return dueMs;
}

/* ── A fachada do plugin ──────────────────────────────────────────────────── */

/**
 * Objeto PLANO com arrow methods, e import ESTÁTICO — as duas coisas por
 * medição, não por estilo (BUG-154): o Proxy que o `registerPlugin` devolve
 * fabrica método para qualquer propriedade, `then` incluído, então resolvê-lo
 * numa promise pendura o `await` para sempre; e o `import()` dinâmico pendurou
 * no WKWebView nos builds 41/42.
 */
const plugin = {
  schedule: (opts: Parameters<typeof LocalNotifications.schedule>[0]) =>
    LocalNotifications.schedule(opts),
  cancel: (opts: Parameters<typeof LocalNotifications.cancel>[0]) =>
    LocalNotifications.cancel(opts),
  checkPermissions: () => LocalNotifications.checkPermissions(),
  requestPermissions: () => LocalNotifications.requestPermissions(),
  registerActionTypes: (opts: Parameters<typeof LocalNotifications.registerActionTypes>[0]) =>
    LocalNotifications.registerActionTypes(opts),
};

/** Estado por processo — nada disto precisa sobreviver a um relaunch. */
let fila: Promise<void> = Promise.resolve();
let pendente: FocusNotificationSnapshot | null | undefined;
let acoesRegistradas = false;
let permissaoNegada = false;
/** O instante já agendado, para não reprogramar o mesmo aviso a cada render. */
let agendadoParaMs: number | null = null;
/**
 * Este processo já mandou cancelar o id fixo (BUG-234). O agendamento vive no
 * SO e sobrevive ao processo: a sessão cancelada/concluída em OUTRO aparelho
 * deixava neste o "registre as páginas" de uma sessão que não existe mais,
 * porque `cancelar` só rodava quando ESTE processo tinha agendado. O primeiro
 * `null` de cada processo cancela às cegas — uma ida ao plugin por boot, e
 * cancelar id sem agendamento é no-op no plugin.
 */
let cancelamentoCego = false;

/**
 * Pede a permissão UMA vez, no momento em que ela é necessária: a pessoa acabou
 * de iniciar uma sessão de foco, que é o contexto em que "avisar quando
 * terminar" se explica sozinho. Pedir na abertura do app seria o convite
 * genérico que o produto já aprendeu a não fazer (BUG-133).
 *
 * ⚠️ O diálogo do sistema é ONE-SHOT por instalação: negado, `requestPermissions`
 * volta a responder `denied` sem desenhar nada. Por isso a recusa é lembrada em
 * memória — insistir não daria segunda chance, só gastaria uma ida ao plugin
 * por sessão.
 */
async function temPermissao(): Promise<boolean> {
  if (permissaoNegada) return false;
  const atual = await plugin.checkPermissions();
  if (atual.display === "granted") return true;
  if (atual.display === "denied") {
    permissaoNegada = true;
    return false;
  }
  const pedido = await plugin.requestPermissions();
  if (pedido.display !== "granted") {
    permissaoNegada = true;
    return false;
  }
  return true;
}

async function registrarAcoes(): Promise<void> {
  if (acoesRegistradas) return;
  await plugin.registerActionTypes({
    types: [
      {
        id: FOCUS_DONE_ACTION_TYPE,
        actions: [
          {
            id: FOCUS_DONE_ACTION_PAGES,
            title: "Marcar páginas",
            // Abre o app: o registro das páginas passa pelo caminho normal do
            // `useFocusTimer`, nunca por um segundo dono (doutrina do
            // `focusDeepLink`, decisão de 23/08).
            foreground: true,
          },
        ],
      },
    ],
  });
  acoesRegistradas = true;
}

async function cancelar(): Promise<void> {
  agendadoParaMs = null;
  await plugin.cancel({ notifications: [{ id: FOCUS_DONE_NOTIFICATION_ID }] });
}

async function convergir(snapshot: FocusNotificationSnapshot | null): Promise<void> {
  const at = resolveFocusNotificationAt(snapshot, Date.now());

  if (at == null) {
    if (agendadoParaMs != null || !cancelamentoCego) {
      cancelamentoCego = true;
      await cancelar();
    }
    return;
  }
  // Já agendado para o mesmo instante: reprogramar não muda nada e gasta uma
  // ida ao plugin a cada render do modal.
  if (agendadoParaMs === at) return;

  if (!(await temPermissao())) return;
  await registrarAcoes();

  const copy = focusDoneNotificationCopy(snapshot?.bookTitle ?? null);
  await plugin.schedule({
    notifications: [
      {
        id: FOCUS_DONE_NOTIFICATION_ID,
        title: copy.title,
        body: copy.body,
        // `allowWhileIdle` para o Doze do Android não segurar o aviso.
        schedule: { at: new Date(at), allowWhileIdle: true },
        /*
         * ⚠️ INEXATO no Android, de propósito — e o default do plugin é o
         * contrário, então isto é escolha, não omissão.
         *
         * Com `isExactNotification: true` (o default), em Android 31+ o plugin
         * **abre a tela de configurações "Alarms & reminders" do sistema** se a
         * permissão de alarme exato ainda não foi concedida. Ou seja: a pessoa
         * toca "Iniciar modo foco" e o app a joga nas configurações do Android —
         * no exato momento em que ela pediu para não ser interrompida.
         *
         * O preço do inexato é o Android poder agrupar o disparo e atrasá-lo
         * alguns minutos. Para ESTE aviso isso não muda nada: a recompensa já
         * foi creditada quando a sessão encerrou, a janela de coleta é de 12 h
         * (F2), e quem está com o app na frente vê o wrap-up sem depender de
         * notificação nenhuma. O aviso é rede de segurança, não despertador.
         *
         * No iOS o campo é ignorado — lá o agendamento é sempre exato.
         */
        isExactNotification: false,
        actionTypeId: FOCUS_DONE_ACTION_TYPE,
        // Diagnóstico e rastro: hoje NENHUM listener de toque lê este campo —
        // o toque só abre o app, e a recuperação decide pelo servidor (a
        // primeira versão deste comentário prometia um consumidor que não
        // existe; BUG-234). Se um dia houver `localNotificationActionPerformed`,
        // é daqui que ele descarta aviso de sessão que já não é a viva.
        extra: { sessionId: snapshot?.sessionId ?? null },
      },
    ],
  });
  agendadoParaMs = at;
}

/**
 * Faz o agendamento convergir para a foto recebida. `null` cancela.
 *
 * Serializado e coalescido: só a foto mais recente converge, e nunca há duas
 * convergências no ar ao mesmo tempo (regra 4 do cabeçalho).
 */
export function syncFocusNotification(
  snapshot: FocusNotificationSnapshot | null,
): Promise<void> {
  if (!FOCUS_LOCAL_NOTIFICATION_READY || !isNativeApp()) return Promise.resolve();
  pendente = snapshot;
  fila = fila.then(async () => {
    if (pendente === undefined) return;
    const foto = pendente;
    pendente = undefined;
    try {
      await convergir(foto);
    } catch (err) {
      // Regra 1: o aviso é conveniência; a sessão nunca paga por ele.
      console.warn("[Lura] agendamento da notificação de foco falhou:", err);
    }
  });
  return fila;
}

/** Só para teste: zera o estado de processo entre casos. */
export function __resetFocusNotificationState(): void {
  fila = Promise.resolve();
  pendente = undefined;
  acoesRegistradas = false;
  permissaoNegada = false;
  agendadoParaMs = null;
  cancelamentoCego = false;
}
